import { useEffect, useId, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { AlertCircle, Check, Globe, Loader2, Plus, X } from 'lucide-react';
import {
  BRAND_IMPORT_API_MESSAGES,
  BRAND_LIMITS,
  normalizeWebsiteUrl,
  WEB_PAGE_FAILURE_TEXT,
  WEBSITE_URL_ERROR_TEXT,
  type BrandImportJobView,
  type BrandImportPageView,
} from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Field';
import { cn } from '../../../lib/cn';
import { URL_SCAN_ENABLED } from '../../brand/flags/urlScan';

export interface UrlListBoxProps {
  /**
   * The rows of the box as typed: one address per row, blank rows included (Next skips them). The box
   * shows one empty row while this is empty. Next sends `scanUrlsFromRows(urls).urls`.
   */
  urls: string[];
  onChange: (urls: string[]) => void;
  disabled?: boolean;
  /** The pages the latest import read, with how each went. */
  previous?: BrandImportJobView['pages'];
}

/** The box's own words (spec:87, :355, :362-368). No string here says "this brand" or "this company". */
export const URL_BOX_COPY = {
  heading: 'Scan web pages for brand voice',
  help: `Up to ${BRAND_LIMITS.urls} public pages, such as your home page and About page. Add one address per row, or paste several, one per line.`,
  rowLabel: (n: number) => `Web page ${n}`,
  placeholder: 'https://www.yourbrand.com/about',
  add: 'Add a web page',
  remove: (n: number) => `Remove web page ${n}`,
  full: `${BRAND_LIMITS.urls} of ${BRAND_LIMITS.urls} web pages. Remove one to add another.`,
  duplicate: "You've already added this page. It's read once.",
  latest: 'Web pages in your latest import',
  read: 'Read',
  reading: 'Reading…',
  notRead: "We couldn't read this page.",
  addAgain: 'Add these pages again',
  added: (n: number) => `Added ${n} web ${n === 1 ? 'page' : 'pages'}.`,
  removed: (address: string) => `Removed ${address}.`,
  alreadyListed: (n: number) => (n === 1 ? '1 address was already in the list.' : `${n} addresses were already in the list.`),
  overflow: (n: number) =>
    `You can scan up to ${BRAND_LIMITS.urls} web pages, so ${n === 1 ? "1 address wasn't" : `${n} addresses weren't`} added.`,
  /** A start with web pages and no document that failed without the server's own sentence. */
  startFailed: "Couldn't start reading your web pages. Try again.",
} as const;

// ── What Next reads (pure) ──────────────────────────────────────────────────────

/** The id of row `index`'s address box: the wizard puts the focus on the first row Next refuses. */
export const urlRowId = (index: number): string => `brand-setup-url-${index}`;

/** How Next reads one row of the box. */
export type UrlRowCheck =
  | { kind: 'blank' }
  | { kind: 'page'; url: string }
  /** The same page as an earlier row once both are normalized: read once. */
  | { kind: 'duplicate'; url: string }
  | { kind: 'invalid'; message: string };

/** A row's address in the form the server reads it (`https://…`), or null when it is not a public web address. */
function pageKey(text: string): string | null {
  const result = normalizeWebsiteUrl(text);
  return result.ok ? result.url : null;
}

/**
 * Each row through the shared website helper (E18), the check the API runs on `urls`: normalized to
 * https://, an earlier row with the same page makes a later one a duplicate, and anything that is not a
 * public web address (another scheme or port, credentials, an IP address, a single-label host such as
 * localhost, an unknown top-level domain) is invalid, with the website box's words.
 */
export function checkUrlRows(rows: readonly string[]): UrlRowCheck[] {
  const seen = new Set<string>();
  return rows.map((text): UrlRowCheck => {
    if (text.trim() === '') return { kind: 'blank' };
    const result = normalizeWebsiteUrl(text);
    if (!result.ok) return { kind: 'invalid', message: WEBSITE_URL_ERROR_TEXT[result.error] };
    if (seen.has(result.url)) return { kind: 'duplicate', url: result.url };
    seen.add(result.url);
    return { kind: 'page', url: result.url };
  });
}

export type ScanUrlsResult =
  | { ok: true; urls: string[] }
  | {
      ok: false;
      /** The first row Next refuses. */
      firstInvalid: number;
      /** The step's message: the API's own sentence for addresses it would refuse. */
      message: string;
    };

/**
 * What Next sends as `urls` (final-design §9; api.startBrandImport): each page once, normalized, in the
 * rows' order; blank rows skipped. A row that is not a public web address stops Next, and so does an
 * address past BRAND_LIMITS.urls (the box never holds more rows), so no typed address is dropped
 * without a word.
 */
export function scanUrlsFromRows(rows: readonly string[]): ScanUrlsResult {
  const checks = checkUrlRows(rows);
  const urls: string[] = [];
  for (let index = 0; index < checks.length; index += 1) {
    const check = checks[index];
    if (check.kind === 'invalid' || (check.kind === 'page' && urls.length >= BRAND_LIMITS.urls)) {
      return { ok: false, firstInvalid: index, message: BRAND_IMPORT_API_MESSAGES.URLS_INVALID };
    }
    if (check.kind === 'page') urls.push(check.url);
  }
  return { ok: true, urls };
}

/** The addresses in pasted text: one per line, trimmed, blank lines left out. */
export function pastedLines(text: string): string[] {
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

export interface InsertRowsOutcome {
  rows: string[];
  /** Rows put in. */
  added: number;
  /** Addresses left out because the list already had the page. */
  duplicates: number;
  /** Addresses left out because the list was full. */
  overflow: number;
}

/**
 * `rows` with `lines` put in at `at`, one per row. A public web address goes in normalized; one whose
 * page the list already has (or an earlier line) is left out; anything else goes in as it is, so its
 * row shows why it cannot be read. Nothing goes past `max` rows.
 */
export function insertRows(
  rows: readonly string[],
  at: number,
  lines: readonly string[],
  max: number = BRAND_LIMITS.urls,
): InsertRowsOutcome {
  const listed = new Set(rows.map(pageKey).filter((key): key is string => key !== null));
  const inserted: string[] = [];
  let duplicates = 0;
  let overflow = 0;
  for (const line of lines) {
    const key = pageKey(line);
    if (key !== null && listed.has(key)) {
      duplicates += 1;
      continue;
    }
    if (rows.length + inserted.length >= max) {
      overflow += 1;
      continue;
    }
    if (key !== null) listed.add(key);
    inserted.push(key ?? line);
  }
  return { rows: [...rows.slice(0, at), ...inserted, ...rows.slice(at)], added: inserted.length, duplicates, overflow };
}

/** A page of the latest import, in words: read, still reading, or why it could not be read (spec:366-368). */
export function pageStatusText(page: Pick<BrandImportPageView, 'status' | 'reason'>): string {
  if (page.status === 'read') return URL_BOX_COPY.read;
  if (page.status === 'pending') return URL_BOX_COPY.reading;
  return page.reason ? WEB_PAGE_FAILURE_TEXT[page.reason] : URL_BOX_COPY.notRead;
}

// ── The box ─────────────────────────────────────────────────────────────────────

interface Notice {
  text: string;
  /** Shown as well as said; otherwise only a screen reader hears it. */
  visible: boolean;
}

/** What a paste or "Add these pages again" did: said always, shown when something was left out. */
function listNotice({ added, duplicates, overflow }: InsertRowsOutcome): Notice | null {
  const said = added > 0 ? URL_BOX_COPY.added(added) : null;
  const shown = [
    duplicates > 0 ? URL_BOX_COPY.alreadyListed(duplicates) : null,
    overflow > 0 ? URL_BOX_COPY.overflow(overflow) : null,
  ].filter((part): part is string => part !== null);
  if (shown.length === 0) return said ? { text: said, visible: false } : null;
  return { text: [said, ...shown].filter((part): part is string => part !== null).join(' '), visible: true };
}

function PageStatusIcon({ status }: { status: BrandImportPageView['status'] }) {
  if (status === 'read') return <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal" aria-hidden />;
  if (status === 'failed') {
    return <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-error-dark" aria-hidden />;
  }
  return <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-ink-muted motion-reduce:animate-none" aria-hidden />;
}

/**
 * "Scan web pages for brand voice" in step 1, under the document box (spec:87, :353-368; web.md §5.8).
 * - Up to BRAND_LIMITS.urls public pages, one address per row. Enter or "Add a web page" opens the next
 *   row; pasting several lines fills one row each, normalized, without a page the list already has and
 *   without going past the limit (a visible note says what was left out).
 * - Leaving a row rewrites a public web address to its `https://` form; anything else shows why under
 *   the row, then follows the typing until it is fixed. A second row with the same page says it is read
 *   once. Nothing here is sent: Next (useWizardController) sends the rows through scanUrlsFromRows,
 *   with the document when one is chosen, in one import, and refuses rows that are not addresses.
 * - The latest import's pages are listed with how each went, in the spec's words for a page that could
 *   not be read, and "Add these pages again" puts them back in the rows (to read them with a new
 *   document, or to fix one that failed).
 * Release 4: renders nothing while features/brand/flags/urlScan.ts is off.
 */
export function UrlListBox(props: UrlListBoxProps) {
  if (!URL_SCAN_ENABLED) return null;
  return <UrlList {...props} />;
}

function UrlList({ urls, onChange, disabled = false, previous }: UrlListBoxProps) {
  const headingId = useId();
  const helpId = useId();
  const rows = urls.length > 0 ? urls : [''];
  const checks = checkUrlRows(rows);
  const full = rows.length >= BRAND_LIMITS.urls;
  const pages = previous ?? [];

  const inputs = useRef(new Map<number, HTMLInputElement>());
  // The row that takes the focus once the rows have re-rendered (after Enter, Add, a paste or a removal).
  const [focusRow, setFocusRow] = useState<number | null>(null);
  // The row being typed in, and whether it already showed a note when the person entered it: a row's note
  // waits until the row is left, then follows the typing until it is fixed.
  const [editing, setEditing] = useState<{ index: number; noted: boolean } | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    if (focusRow === null) return;
    inputs.current.get(Math.max(0, Math.min(focusRow, rows.length - 1)))?.focus();
    setFocusRow(null);
  }, [focusRow, rows.length]);

  const listedKeys = new Set(checks.flatMap((check) => (check.kind === 'page' ? [check.url] : [])));
  const canAddAgain = !full && pages.some((page) => !listedKeys.has(pageKey(page.url) ?? page.url));

  const edit = (index: number, value: string) => {
    setNotice(null);
    onChange(rows.map((row, i) => (i === index ? value : row)));
  };

  const leave = (index: number) => {
    setEditing((now) => (now?.index === index ? null : now));
    const text = rows[index];
    if (text === undefined) return;
    const key = pageKey(text);
    if (key !== null && key !== text) onChange(rows.map((row, i) => (i === index ? key : row)));
  };

  const remove = (index: number) => {
    const address = rows[index]?.trim() ?? '';
    const next = rows.filter((_, i) => i !== index);
    onChange(next);
    setNotice(address ? { text: URL_BOX_COPY.removed(address), visible: false } : null);
    setFocusRow(Math.min(index, Math.max(0, next.length - 1)));
  };

  const add = () => {
    if (full) return;
    const last = rows.length - 1;
    // An empty row at the end already waits for an address.
    if (rows[last].trim() === '') {
      setFocusRow(last);
      return;
    }
    onChange([...rows, '']);
    setFocusRow(rows.length);
  };

  const onKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (rows[index].trim() === '') return;
    if (index + 1 < rows.length && rows[index + 1].trim() === '') {
      setFocusRow(index + 1);
      return;
    }
    if (full) return;
    onChange([...rows.slice(0, index + 1), '', ...rows.slice(index + 1)]);
    setFocusRow(index + 1);
  };

  const onPaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    const lines = pastedLines(event.clipboardData.getData('text/plain'));
    // One address is pasted as usual.
    if (lines.length < 2) return;
    event.preventDefault();
    const own = rows[index] ?? '';
    const input = event.currentTarget;
    // The lines take the row's place when it is empty or wholly selected; otherwise they follow it.
    const replace = own.trim() === '' || ((input.selectionStart ?? 0) === 0 && (input.selectionEnd ?? 0) >= own.length);
    const base = replace ? rows.filter((_, i) => i !== index) : [...rows];
    const at = replace ? index : index + 1;
    const outcome = insertRows(base, at, lines);
    if (outcome.added > 0) {
      onChange(outcome.rows);
      setFocusRow(at + outcome.added - 1);
    }
    setNotice(listNotice(outcome));
  };

  const addAgain = () => {
    const base = rows.filter((row) => row.trim() !== '');
    const outcome = insertRows(
      base,
      base.length,
      pages.map((page) => page.url),
    );
    if (outcome.added > 0) {
      onChange(outcome.rows);
      setFocusRow(base.length);
    }
    setNotice(listNotice(outcome));
  };

  return (
    <section aria-labelledby={headingId} className="space-y-3 rounded-xl border border-line bg-page p-4">
      <div>
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
          <h3 id={headingId} className="text-sm font-semibold text-ink-strong">
            {URL_BOX_COPY.heading}
          </h3>
        </div>
        <p id={helpId} className="mt-1 text-sm text-ink-muted">
          {URL_BOX_COPY.help}
        </p>
      </div>

      {pages.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-ink-label">{URL_BOX_COPY.latest}</p>
          <ul className="space-y-2">
            {pages.map((page, index) => (
              <li key={`${index}:${page.url}`} className="flex items-start gap-2">
                <PageStatusIcon status={page.status} />
                <div className="min-w-0">
                  <p className="break-all text-sm text-ink">{page.url}</p>
                  <p
                    className={cn(
                      'text-xs',
                      page.status === 'failed' ? 'text-red-600 dark:text-error-dark' : 'text-ink-muted',
                    )}
                  >
                    {pageStatusText(page)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <Button type="button" variant="plain" size="sm" disabled={disabled || !canAddAgain} onClick={addAgain}>
            {URL_BOX_COPY.addAgain}
          </Button>
        </div>
      )}

      <ul className="space-y-2">
        {rows.map((text, index) => {
          const check = checks[index];
          const id = urlRowId(index);
          const noteId = `${id}-note`;
          // A row being typed in shows no new note until it is left.
          const quiet = editing?.index === index && !editing.noted;
          const error = check.kind === 'invalid' && !quiet ? check.message : null;
          const duplicate = check.kind === 'duplicate' && !quiet;
          return (
            // By position: rows are edited in place, and two rows may hold the same text.
            <li key={index}>
              <div className="flex items-center gap-2">
                <Input
                  ref={(el) => {
                    if (el) inputs.current.set(index, el);
                    else inputs.current.delete(index);
                  }}
                  id={id}
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={text}
                  disabled={disabled}
                  placeholder={URL_BOX_COPY.placeholder}
                  aria-label={URL_BOX_COPY.rowLabel(index + 1)}
                  aria-describedby={error || duplicate ? `${helpId} ${noteId}` : helpId}
                  aria-invalid={error ? true : undefined}
                  onChange={(e) => edit(index, e.target.value)}
                  onFocus={() => setEditing({ index, noted: check.kind === 'invalid' || check.kind === 'duplicate' })}
                  onBlur={() => leave(index)}
                  onKeyDown={onKeyDown(index)}
                  onPaste={onPaste(index)}
                  className="min-w-0 flex-1"
                />
                {(rows.length > 1 || text.trim() !== '') && (
                  <Button
                    type="button"
                    variant="plain"
                    size="sm"
                    className="w-8 shrink-0 px-0"
                    disabled={disabled}
                    aria-label={URL_BOX_COPY.remove(index + 1)}
                    onClick={() => remove(index)}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </div>
              {error && (
                <p id={noteId} className="mt-1 text-xs text-red-600 dark:text-error-dark">
                  {error}
                </p>
              )}
              {duplicate && (
                <p id={noteId} className="mt-1 text-xs text-ink-muted">
                  {URL_BOX_COPY.duplicate}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" disabled={disabled || full} onClick={add}>
          <Plus className="h-4 w-4" aria-hidden />
          {URL_BOX_COPY.add}
        </Button>
        {full && <p className="text-sm text-ink-muted">{URL_BOX_COPY.full}</p>}
      </div>

      {/* Always in the page, so what a paste or a removal did is read out. */}
      <p role="status" className={notice?.visible ? 'text-sm text-ink-muted' : 'sr-only'}>
        {notice?.text ?? ''}
      </p>
    </section>
  );
}
